import type { Contract, ContractMilestone } from '../types/contract';
import type { ProposalContent } from '../types/proposal-blocks';

/**
 * Generates the full contract HTML from a Contract record + proposal content.
 * The contract auto-fills with client data, project details, milestones and payment terms.
 */
export function generateContractHTML(
  contract: Contract,
  proposalContent: ProposalContent,
  clientSignatureUrl?: string
): string {
  const contractDate = new Date(contract.signed_at || contract.created_at);
  const day = contractDate.getDate();
  const month = contractDate.toLocaleDateString('es-ES', { month: 'long' });
  const year = contractDate.getFullYear();

  const client = contract.client_data;
  const payment = contract.payment_terms;
  const totalAmount = contract.total_amount;
  const formattedAmount = totalAmount.toLocaleString('es-ES', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

  // Extract project info from proposal content
  const projectTitle = proposalContent.meta.project_name || 'Desarrollo de Software Personalizado';
  const brandName = proposalContent.meta.brand || 'Cero Ideas';

  // Extract milestones from proposal blocks
  const milestonesBlock = proposalContent.blocks.find((b) => b.type === 'milestones') as
    | Extract<ProposalContent['blocks'][number], { type: 'milestones' }>
    | undefined;
  const milestones: ContractMilestone[] = milestonesBlock
    ? milestonesBlock.items.map((m) => ({
        id: m.id,
        title: m.title,
        date: m.date,
        amount: m.amount,
        status: m.status,
      }))
    : [];

  // Extract duration from milestones metrics or fallback
  const durationMetric = milestonesBlock?.metrics?.find(
    (m) => m.label.toLowerCase().includes('duración') || m.label.toLowerCase().includes('plazo')
  );
  const duration = durationMetric?.value || `${milestones.length} fases`;

  // Extract project description from first section block
  const descriptionBlock = proposalContent.blocks.find(
    (b) => b.type === 'section' && b.id !== 'hero'
  ) as Extract<ProposalContent['blocks'][number], { type: 'section' }> | undefined;
  const projectDescription = descriptionBlock?.body_md || '';

  // Extract package details for the annex
  const packageBlocks = proposalContent.blocks.filter((b) => b.type === 'package_page') as Extract<
    ProposalContent['blocks'][number],
    { type: 'package_page' }
  >[];

  return `
<!DOCTYPE html>
<html lang="es">
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <title>Contrato ${contract.contract_number}</title>
  <style>
    @page { size: A4; margin: 2.5cm; }
    body { font-family: 'Times New Roman', serif; font-size: 12pt; line-height: 1.6; color: #000; margin: 0; padding: 20px; }
    h1 { text-align: center; font-size: 16pt; font-weight: bold; margin: 20px 0 30px 0; text-transform: uppercase; }
    h2 { font-size: 13pt; font-weight: bold; margin: 25px 0 10px 0; text-transform: uppercase; }
    h3 { font-size: 12pt; font-weight: bold; margin: 15px 0 8px 0; color: #1e40af; }
    p { margin: 10px 0; text-align: justify; }
    .header-date { text-align: right; margin-bottom: 20px; }
    .section { margin: 20px 0; }
    .clause-title { font-weight: bold; text-transform: uppercase; margin: 20px 0 10px 0; }
    .signature-section { margin-top: 60px; page-break-inside: avoid; }
    .signature-box { margin: 30px 0; padding: 20px; border: 1px solid #ccc; min-height: 120px; display: inline-block; width: 45%; }
    .signature-box.left { margin-right: 5%; }
    .signature-label { font-weight: bold; margin-bottom: 10px; }
    .signature-image { max-width: 250px; max-height: 80px; margin: 10px 0; }
    .contract-number { text-align: right; font-size: 10pt; color: #666; margin-bottom: 10px; }
    ul { margin: 10px 0 10px 20px; }
    li { margin: 5px 0; }
    .payment-table { width: 100%; border-collapse: collapse; margin: 15px 0; }
    .payment-table td { padding: 8px; border: 1px solid #ddd; }
    .payment-table td:first-child { font-weight: bold; width: 40%; }
    .highlight { background-color: #f0f7ff; padding: 15px; border-left: 4px solid #1e40af; margin: 15px 0; }
    .eidas-notice { margin-top: 30px; padding: 12px; background: #f8f9fa; border: 1px solid #e9ecef; font-size: 9pt; color: #666; text-align: center; }
  </style>
</head>
<body>
  <div class="contract-number">
    Numero de contrato: ${contract.contract_number}
  </div>

  <h1>Contrato de Prestacion de Servicios para el Desarrollo de Software</h1>

  <div class="header-date">
    En Velez-Malaga, a ${day} de ${month} de ${year}
  </div>

  <h2>Reunidos</h2>

  <div class="section">
    <p>
      De una parte, <strong>${client.name || '________________________'}</strong>, mayor de edad,
      con domicilio en <strong>${client.address || '________________________'}</strong> y
      NIF <strong>${client.nif || '________________'}</strong>, en calidad de
      <strong>REPRESENTANTE del CLIENTE</strong>${client.position ? ` (${client.position})` : ''}
      ${client.company ? ` de la empresa <strong>${client.company}</strong>` : ''}
      (en adelante, "EL CLIENTE").
    </p>

    <p>
      Y de otra parte, <strong>Illya Alvarado</strong>, en representacion de la empresa
      <strong>CERO IDEAS, SOLUCIONES TECNOLOGICAS E INVERSIONES, SL</strong>, con CIF <strong>B70681770</strong>
      y domicilio social en <strong>Lugar Cortijo Los Gomez 546, 29792 Velez-Malaga (Malaga)</strong>,
      correo electronico <strong>info@ceroideas.es</strong> (en adelante, "EL CONTRATISTA").
    </p>

    <p>
      Ambas partes, en adelante "las Partes", reconociendose capacidad legal suficiente, acuerdan
      suscribir el presente Contrato de Prestacion de Servicios de Desarrollo de Software, que se
      regira por las siguientes:
    </p>
  </div>

  <h2>Clausulas</h2>

  <div class="clause-title">PRIMERA. Objeto</div>
  <p>
    EL CONTRATISTA se compromete a prestar los servicios de diseno, desarrollo y puesta en
    marcha del proyecto "<strong>${projectTitle}</strong>", conforme a los requerimientos
    funcionales acordados con EL CLIENTE y definidos en la propuesta tecnica aceptada.
  </p>

  ${projectDescription ? `
  <div class="highlight">
    <h3>Descripcion del Proyecto</h3>
    <div>${projectDescription.replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')}</div>
  </div>
  ` : ''}

  <div class="clause-title">SEGUNDA. Fases, presupuesto y forma de pago</div>
  <p>
    Las fases del proyecto, su duracion estimada y los importes correspondientes se detallan en
    el presupuesto aceptado por EL CLIENTE, que forma parte integral del presente contrato
    como <strong>Anexo I</strong>.
  </p>

  <div class="highlight">
    <h3>Desglose Economico</h3>
    <table class="payment-table">
      <tr>
        <td>Coste total del proyecto</td>
        <td><strong>${formattedAmount} EUR + IVA</strong></td>
      </tr>
      <tr>
        <td>Duracion estimada</td>
        <td><strong>${duration}</strong></td>
      </tr>
      ${milestones.length > 0 ? `
      <tr>
        <td>Numero de hitos principales</td>
        <td><strong>${milestones.length}</strong></td>
      </tr>
      ` : ''}
    </table>
  </div>

  <p><strong>Forma de pago:</strong></p>
  <p>
    El proyecto se abonara en tres pagos distribuidos de la siguiente manera:
  </p>
  <ul>
    <li><strong>Primer pago (40%):</strong> ${payment.first_payment.toLocaleString('es-ES')} EUR + IVA al inicio del proyecto</li>
    <li><strong>Segundo pago (30%):</strong> ${payment.second_payment.toLocaleString('es-ES')} EUR + IVA en la fase intermedia, tras validacion de hitos</li>
    <li><strong>Tercer pago (30%):</strong> ${payment.third_payment.toLocaleString('es-ES')} EUR + IVA al finalizar y entregar el proyecto completo</li>
  </ul>
  <p>
    Cada pago debera realizarse previa validacion por parte de EL CLIENTE de los entregables correspondientes a cada fase.
  </p>

  <div class="clause-title">TERCERA. Plazos de entrega</div>
  <p>
    La duracion total estimada del proyecto es de <strong>${duration}</strong>. Los plazos especificos
    de cada fase se detallan en el Anexo I (presupuesto aceptado). Cualquier modificacion del alcance
    que implique un cambio de plazo debera ser pactada y aprobada por ambas partes por escrito.
  </p>

  <div class="clause-title">CUARTA. Entrega del codigo y propiedad intelectual</div>
  <p>
    EL CONTRATISTA entregara el codigo fuente completo y funcional al finalizar el proyecto y
    tras la recepcion del ultimo pago pendiente. La propiedad intelectual del software pasara a
    ser de EL CLIENTE, salvo librerias o componentes de terceros con licencia publica.
  </p>
  <p>
    La entrega se realizara mediante repositorio Git, incluyendo toda la documentacion tecnica necesaria.
  </p>

  <div class="clause-title">QUINTA. Garantia y soporte</div>
  <p>
    EL CONTRATISTA ofrece una garantia de <strong>180 dias naturales</strong> desde la fecha de entrega final
    para corregir errores atribuibles al desarrollo original. Esta garantia no incluye nuevas funcionalidades
    ni cambios en los requisitos iniciales.
  </p>
  <p>
    Cualquier evolutivo, mantenimiento o soporte adicional podra ser contratado bajo un nuevo
    acuerdo o mediante bolsa de horas. Las tarifas de mantenimiento son:
  </p>
  <ul>
    <li>Paquetes de horas: 10h o 20h a 55 EUR/h</li>
    <li>Urgencias en fin de semana: 65 EUR/h</li>
  </ul>

  <div class="clause-title">SEXTA. Confidencialidad</div>
  <p>
    Ambas partes se obligan a mantener en estricta confidencialidad toda la informacion
    tecnica, comercial y operativa a la que tengan acceso durante la ejecucion del contrato.
    Esta obligacion permanecera vigente incluso despues de la finalizacion del mismo.
  </p>

  <div class="clause-title">SEPTIMA. Legislacion aplicable y jurisdiccion</div>
  <p>
    El presente contrato se regira por la legislacion espanola. Las partes se someten a los
    Juzgados y Tribunales de Malaga capital para la resolucion de cualquier controversia que
    pudiera surgir de la interpretacion o ejecucion del presente contrato.
  </p>

  <div class="signature-section">
    <p>
      Y en prueba de conformidad, ambas partes firman el presente contrato por duplicado y a un
      solo efecto, en el lugar y fecha indicados.
    </p>

    <div style="margin-top: 50px;">
      <div class="signature-box left">
        <div class="signature-label">Fdo. EL CLIENTE:</div>
        ${clientSignatureUrl ? `<img src="${clientSignatureUrl}" class="signature-image" alt="Firma del cliente" />` : '<div style="height: 80px;"></div>'}
        <div style="margin-top: 10px;">
          <strong>${client.name || '________________________'}</strong><br/>
          ${client.company || ''}
          ${client.position ? `<br/>${client.position}` : ''}
        </div>
      </div>

      <div class="signature-box">
        <div class="signature-label">Fdo. EL CONTRATISTA:</div>
        <div style="height: 80px; display: flex; align-items: center;">
          <div style="font-family: 'Brush Script MT', cursive; font-size: 24pt;">
            Illya Alvarado
          </div>
        </div>
        <div style="margin-top: 10px;">
          <strong>Illya Alvarado</strong><br/>
          CEO - Cero Ideas<br/>
          CERO IDEAS, SOLUCIONES TECNOLOGICAS E INVERSIONES, SL
        </div>
      </div>
    </div>
  </div>

  ${milestones.length > 0 || packageBlocks.length > 0 ? `
  <div style="page-break-before: always;"></div>

  <h2>Anexo I - Desglose del Proyecto</h2>

  <div class="section">
    <h3>Proyecto: ${projectTitle}</h3>
    ${client.company ? `<p><strong>Empresa Cliente:</strong> ${client.company}</p>` : ''}
    ${client.name ? `<p><strong>Responsable:</strong> ${client.name}</p>` : ''}
    <p><strong>Duracion:</strong> ${duration}</p>
  </div>

  ${milestones.length > 0 ? `
  <h3>Hitos y Entregables</h3>
  ${milestones.map((m, i) => `
    <div class="section">
      <h3>Hito ${i + 1}: ${m.title}</h3>
      ${m.amount ? `<p><strong>Importe:</strong> ${m.amount}</p>` : ''}
      ${m.date ? `<p><strong>Fecha estimada:</strong> ${m.date}</p>` : ''}
    </div>
  `).join('')}
  ` : ''}

  ${packageBlocks.length > 0 ? `
  <h3>Paquetes de Servicio</h3>
  ${packageBlocks.map((pkg) => `
    <div class="section">
      <h3>${pkg.name}</h3>
      <p style="font-size: 11pt; color: #666;">${pkg.price || ''} ${pkg.hours ? `· ${pkg.hours}` : ''}</p>
      <ul>
        ${pkg.tasks.map((t) => `
          <li>
            <strong>${t.name}</strong>
            ${t.description ? `<br/>${t.description}` : ''}
            ${t.hours ? `<br/><em>Esfuerzo: ${t.hours}</em>` : ''}
          </li>
        `).join('')}
      </ul>
    </div>
  `).join('')}
  ` : ''}

  <div class="highlight" style="margin-top: 30px;">
    <h3>Resumen Total</h3>
    <table class="payment-table">
      <tr>
        <td>Inversion Total</td>
        <td><strong>${formattedAmount} EUR + IVA</strong></td>
      </tr>
    </table>
  </div>
  ` : ''}

  <div class="eidas-notice">
    Este documento constituye una firma electronica simple conforme al Reglamento eIDAS (UE) 910/2014.
    La IP y user-agent del firmante han sido registrados en el sistema.
  </div>

</body>
</html>
  `;
}

/**
 * Validates Spanish NIF, NIE or CIF format.
 */
export function validateNIF(nif: string): boolean {
  const cleanNif = nif.toUpperCase().replace(/[\s-]/g, '');

  const nifRegex = /^[0-9]{8}[A-Z]$/;
  const nieRegex = /^[XYZ][0-9]{7}[A-Z]$/;
  const cifRegex = /^[ABCDEFGHJNPQRSUVW][0-9]{7}[0-9A-J]$/;

  if (!nifRegex.test(cleanNif) && !nieRegex.test(cleanNif) && !cifRegex.test(cleanNif)) {
    return false;
  }

  if (nifRegex.test(cleanNif)) {
    const letters = 'TRWAGMYFPDXBNJZSQVHLCKE';
    const number = parseInt(cleanNif.substr(0, 8), 10);
    const letter = cleanNif.substr(8, 1);
    return letters[number % 23] === letter;
  }

  return true;
}
